import { MAP_TILES_DB, getTileKey } from '../db/mapTiles';
/**
 * Extract coordinates from check-in tasks and calculate bounding box
 */
export const calculateBoundingBox = (tasks) => {
    const checkInTasks = tasks.filter((task) => task.taskType === 'check-in' && task.coordinatesLat !== 0 && task.coordinatesLon !== 0);
    if (checkInTasks.length === 0) {
        return null;
    }
    let minLat = checkInTasks[0].coordinatesLat;
    let maxLat = checkInTasks[0].coordinatesLat;
    let minLng = checkInTasks[0].coordinatesLon;
    let maxLng = checkInTasks[0].coordinatesLon;
    checkInTasks.forEach((task) => {
        if (task.coordinatesLat < minLat)
            minLat = task.coordinatesLat;
        if (task.coordinatesLat > maxLat)
            maxLat = task.coordinatesLat;
        if (task.coordinatesLon < minLng)
            minLng = task.coordinatesLon;
        if (task.coordinatesLon > maxLng)
            maxLng = task.coordinatesLon;
    });
    // Add small buffer (tight bounding box as per user preference)
    const latBuffer = (maxLat - minLat) * 0.25 || 0.001; // 35% buffer or minimum
    const lngBuffer = (maxLng - minLng) * 0.25 || 0.001;
    return {
        minLat: minLat - latBuffer,
        maxLat: maxLat + latBuffer,
        minLng: minLng - lngBuffer,
        maxLng: maxLng + lngBuffer,
    };
};
/**
 * Convert lat/lng bounds to tile coordinates for a specific zoom level
 */
const getTileRange = (bounds, zoom) => {
    const tiles = [];
    // Convert lat/lng to tile coordinates using standard Mercator projection
    const lat2tile = (lat, zoom) => {
        return Math.floor(((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
            Math.pow(2, zoom));
    };
    const lng2tile = (lng, zoom) => {
        return Math.floor(((lng + 180) / 360) * Math.pow(2, zoom));
    };
    const minX = lng2tile(bounds.minLng, zoom);
    const maxX = lng2tile(bounds.maxLng, zoom);
    const minY = lat2tile(bounds.maxLat, zoom); // Note: Y is inverted
    const maxY = lat2tile(bounds.minLat, zoom);
    for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
            tiles.push({ z: zoom, x, y });
        }
    }
    return tiles;
};
/**
 * Download a single tile and store it in IndexedDB
 */
const downloadTile = async (coords, tileUrl) => {
    try {
        const url = tileUrl
            .replace('{z}', coords.z.toString())
            .replace('{x}', coords.x.toString())
            .replace('{y}', coords.y.toString())
            .replace('{s}', ['a', 'b', 'c'][Math.floor(Math.random() * 3)]); // Random subdomain
        const response = await fetch(url);
        if (!response.ok) {
            console.warn(`[Tile Caching] Failed to download tile ${getTileKey(coords)}: ${response.status}`);
            return { success: false, coords };
        }
        const blob = await response.blob();
        await MAP_TILES_DB.SET.tile(coords, {
            url,
            blob,
            timestamp: Date.now(),
        });
        return { success: true, coords };
    }
    catch (error) {
        console.error(`[Tile Caching] Error downloading tile ${getTileKey(coords)}:`, error);
        return { success: false, coords };
    }
};
/**
 * Download all tiles for the specified bounds and zoom levels
 */
export const downloadTilesForRally = async (rallyId, bounds, zoomLevels = [15, 16, 17, 18], tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', onProgress) => {
    console.log('[Tile Caching] Starting download for rally', rallyId, bounds, zoomLevels);
    // Calculate all tiles needed
    const allTiles = [];
    zoomLevels.forEach((zoom) => {
        const tiles = getTileRange(bounds, zoom);
        allTiles.push(...tiles);
    });
    const totalTiles = allTiles.length;
    let downloaded = 0;
    let failed = 0;
    console.log(`[Tile Caching] Total tiles to download: ${totalTiles}`);
    // Download tiles in batches to avoid overwhelming the server
    const BATCH_SIZE = 10;
    const DELAY_BETWEEN_BATCHES = 500; // ms
    for (let i = 0; i < allTiles.length; i += BATCH_SIZE) {
        const batch = allTiles.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(batch.map((coords) => downloadTile(coords, tileUrl)));
        results.forEach((result) => {
            if (result.success) {
                downloaded++;
            }
            else {
                failed++;
            }
        });
        const progress = {
            total: totalTiles,
            downloaded,
            failed,
            percentage: Math.round((downloaded / totalTiles) * 100),
        };
        console.log(`[Tile Caching] Progress: ${progress.percentage}% (${downloaded}/${totalTiles})`);
        if (onProgress) {
            onProgress(progress);
        }
        // Delay between batches to be respectful to OSM servers
        if (i + BATCH_SIZE < allTiles.length) {
            await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
    }
    // Store metadata
    await MAP_TILES_DB.SET.metadata(rallyId, {
        bounds,
        zoomLevels,
        tileCount: downloaded,
    });
    const finalStats = {
        total: totalTiles,
        downloaded,
        failed,
        percentage: Math.round((downloaded / totalTiles) * 100),
    };
    console.log('[Tile Caching] Download complete:', finalStats);
    return {
        success: failed === 0,
        stats: finalStats,
    };
};
/**
 * Check if tiles are already cached for a rally
 */
export const getTileCacheStatus = async (rallyId) => {
    const metadata = await MAP_TILES_DB.GET.metadata(rallyId);
    const tileCount = await MAP_TILES_DB.GET.tileCount();
    return {
        isCached: metadata !== null && tileCount > 0,
        metadata,
        tileCount,
    };
};
/**
 * Get a cached tile blob URL for use in Leaflet
 */
export const getCachedTileUrl = async (coords) => {
    const tileData = await MAP_TILES_DB.GET.tile(coords);
    if (!tileData) {
        return null;
    }
    // Create blob URL for the cached tile
    return URL.createObjectURL(tileData.blob);
};
/**
 * Clear all cached tiles (useful for cleanup)
 */
export const clearAllCachedTiles = async () => {
    console.log('[Tile Caching] Clearing all cached tiles');
    await MAP_TILES_DB.CLEAR.all();
};
/**
 * Download and cache map marker assets
 */
export const cacheMapAssets = async (assets) => {
    console.log('[Asset Caching] Starting download for map assets');
    for (const asset of assets) {
        try {
            const response = await fetch(asset.url);
            if (!response.ok) {
                console.warn(`[Asset Caching] Failed to download asset ${asset.key}: ${response.status}`);
                continue;
            }
            const blob = await response.blob();
            await MAP_TILES_DB.SET.asset(asset.key, {
                url: asset.url,
                blob,
                timestamp: Date.now(),
            });
            console.log(`[Asset Caching] Cached asset: ${asset.key}`);
        }
        catch (error) {
            console.error(`[Asset Caching] Error downloading asset ${asset.key}:`, error);
        }
    }
    console.log('[Asset Caching] Asset caching complete');
};
/**
 * Get a cached asset blob URL for use in Leaflet
 */
export const getCachedAssetUrl = async (assetKey) => {
    const assetData = await MAP_TILES_DB.GET.asset(assetKey);
    if (!assetData) {
        return null;
    }
    // Create blob URL for the cached asset
    return URL.createObjectURL(assetData.blob);
};
